import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Patient } from '@models';
import { GenderName, Race, SexAssignedAtBirth } from '@enums';
import { AlertService } from '../../../core/services/alert.service';
import StatusChip from '../PatientInfoMembership/StatusChip';
import { environment } from '../../../../environments/environment';

export type PatientInfoSummaryProps = PropsWithChildren<{
  alertService: AlertService;
  patient: Patient;
  patientDisplayAge: string | undefined;
}>;

const standardDateFormat = new Intl.DateTimeFormat('en-US', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
});

const DEFAULT_TEXT_SEPARATOR_CHARACTER = '•';

export function PatientInfoSummary({
  alertService,
  patient,
  patientDisplayAge,
}: PatientInfoSummaryProps): React.JSX.Element {
  function isDateValid(date: Date | string | number) {
    return !isNaN(new Date(date).getDate());
  }

  const displayStandardDate = useCallback((dateToFormat: string | number | Date): string => {
    const date = new Date(dateToFormat);
    if (!isDateValid(date)) {
      return dateToFormat?.toString();
    }
    return standardDateFormat.format(date);
  }, []);

  function displayPhoneNumber(phoneNumber: string): string {
    if (phoneNumber) {
      let phone = phoneNumber.replace(/\D/g, '');
      const match = phone.match(/^(\d{1,3})(\d{0,3})(\d{0,4})$/);
      if (match) {
        phone = `(${match[1]}${match[2] ? ') ' : ''}${match[2]}${match[3] ? '-' : ''}${match[3]}`;
      }
      return phone;
    }

    return phoneNumber;
  }

  function handleCopyPhoneNumber(phoneNumber: string): () => void {
    return () => {
      copyPhoneNumber(phoneNumber);
    };
  }

  async function copyPhoneNumber(phoneNumber: string): Promise<void> {
    await navigator.clipboard.writeText(phoneNumber);
    alertService.success('Phone number copied to the clipboard');
  }

  function get65PlusInfoMessage(): string {
    // eslint-disable-next-line max-len
    return 'Patient is 65+ \n\n• Limit Orders to services that are 100% Nice paid \n• Limit Prescriptions to the Nice Free Drug List \n• Not eligible for Diabetes Education';
  }

  const dateOfBirthAndAge = useMemo(() => {
    let age = displayStandardDate(patient.user?.dateOfBirth ? patient.user.dateOfBirth : patient.dateOfBirth);

    if (patientDisplayAge) {
      age = age + ' ' + DEFAULT_TEXT_SEPARATOR_CHARACTER + ' ' + patientDisplayAge;
    }

    return age;
  }, [patient, patientDisplayAge, displayStandardDate]);

  return (
    <>
      <Box>
        <Typography variant={'h6'} sx={{ fontSize: '22px', fontWeight: 400 }}>
          {patient.name}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', marginTop: '8px' }}>
        <Typography component={'div'} variant={'body1'}>
          {patient.id}
        </Typography>
        {patient?.deleted && (
          <Box sx={{ marginLeft: '8px' }}>
            <StatusChip status={'LEGACY_DELETED'} />
          </Box>
        )}
        {!patient.deleted && !patient.active && environment.featuresEnabled.showLegacyMembershipStatus && (
          <Box sx={{ marginLeft: '8px' }}>
            <StatusChip status={'LEGACY_DEACTIVATED'} />
          </Box>
        )}
        {patient?.is65Plus && (
          <Box sx={{ marginLeft: '10px' }}>
            <Tooltip
              title={get65PlusInfoMessage()}
              componentsProps={{
                tooltip: {
                  sx: {
                    'white-space': 'pre',
                  },
                },
              }}
            >
              <IconButton sx={{ paddingTop: 0, paddingRight: '8px', paddingBottom: 0, paddingLeft: 0 }}>
                <StatusChip status={'LEGACY_65_PLUS'} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {patient?.unifiedPreferredName && (
        <Box sx={{ marginTop: '8px' }}>
          <Typography component={'span'} variant={'body2'} sx={{ fontWeight: 'bold' }}>
            Preferred Name:
          </Typography>
          <Typography component={'span'} variant={'body2'}>
            {' ' + patient.unifiedPreferredName}
          </Typography>
        </Box>
      )}
      <Box sx={{ marginTop: '2px' }}>
        <Typography component={'span'} variant={'body2'} sx={{ fontWeight: 'bold' }}>
          DOB:
        </Typography>
        <Typography component={'span'} variant={'body2'}>
          {' ' + dateOfBirthAndAge}
        </Typography>
      </Box>
      <Box sx={{ marginTop: '2px' }}>
        <Typography component={'span'} variant={'body2'} sx={{ fontWeight: 'bold' }}>
          Sex assigned at birth:
        </Typography>
        <Typography component={'span'} variant={'body2'}>
          {' ' + SexAssignedAtBirth[patient.sexAssignedAtBirth]?.valueOf()}
        </Typography>
      </Box>
      <Box sx={{ marginTop: '2px' }}>
        <Typography component={'span'} variant={'body2'} sx={{ fontWeight: 'bold' }}>
          Gender:
        </Typography>
        <Typography component={'span'} variant={'body2'}>
          {' ' + Object.values(GenderName)[patient.gender]}
        </Typography>
      </Box>
      {patient.race !== null && (
        <Box sx={{ marginTop: '2px' }}>
          <Typography component={'span'} variant={'body2'} sx={{ fontWeight: 'bold' }}>
            Race:
          </Typography>
          <Typography component={'span'} variant={'body2'}>
            {' ' + Race[patient.race]}
          </Typography>
        </Box>
      )}
    </>
  );
}

export default PatientInfoSummary;
